import React, { Fragment, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import CLOSE from "../../../assets/images/icons/close.svg";
import LOGO from "../../../assets/images/icons/ogwugo-o.svg";
import FLUTTER from "../../../assets/images/icons/flutter.svg";
import PAYSTACK from "../../../assets/images/icons/paystack.svg";
import TRANSFER from "../../../assets/images/icons/bank-transfer.svg";
import Warning from "../../../assets/images/icons/warning-circle.svg";
import PaymentSkeleton from "../../skeleton/payment";
import PaystackPlugin from "../../../pages/common/payments/Paystack";
import FlutterwavePlugin from "../../../pages/common/payments/Flutterwave";
import PaymentSuccess from "../guestCheckout/PaymentSuccess";
import PaymentFailure from "../guestCheckout/PaymentFailure";
import { cleanInput } from "../../../helpers/cleanInput";
import PaystackTransferPlugin from "../../../pages/common/payments/PaystackBankTransfer";
import { showApplication } from "../../../redux/applicationSettings/applicationAction";
import { checkoutPublicCart } from "../../../redux/publicCart/publicCartAction";
import { getAllPaymentProviders } from "../../../redux/payment/paymentActions";
import { storePublicCartProducts } from "../../../redux/cart/cartAction";

export default React.forwardRef(function PaymentGuestCartModal(
  {
    visibility = false,
    primaryButtonTitle,
    primaryButton,
    primaryButtonStyle,
    primaryButtonClassName,
    modalTitle,
    modalTitleSection,
    modelContent,
    modelContentSection,
    disableClose = false,
    disableOpen = false,
    onOpen = () => {},
    onClose = () => {},
    payment_type,
    receipt_email,
    orderData,
  },
  ref
) {
  // Ref handler
  React.useImperativeHandle(ref, () => ({
    open: () => openModal(),
    close: () => closeModal(),
    getTotalPrice: (data) => getTotalPrice(data),
    getReference: (data) => getReferenceId(data),
  }));

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const paystackRef = useRef(null);
  const flutterRef = useRef(null);
  const paymentSuccessRef = useRef(null);
  const paymentFailureRef = useRef(null);
  const paystackTransferRef = useRef(null);
  let [isOpen, setIsOpen] = React.useState(visibility);
  let [totalPrice, setTotalPrice] = React.useState("");
  let [reference_id, setReferenceId] = React.useState("");
  const { settings, isLoading } = useSelector((state) => state.application);
  const { allProviders } = useSelector((state) => state.payment);

  useEffect(() => {
    dispatch(showApplication());
    dispatch(getAllPaymentProviders());
    // eslint-disable-next-line
  }, []);

  function closeModal() {
    !disableClose && setIsOpen(false);
    !disableClose && onClose();
  }

  function openModal() {
    !disableOpen && setIsOpen(true);
    !disableOpen && onOpen();
  }

  function getTotalPrice(data) {
    setTotalPrice(data);
  }

  function getReferenceId(data) {
    setReferenceId(data);
  }

  function goToBank() {
    navigate(`/bank-transfer`, {
      state: { totalPrice: totalPrice },
    });
  }

  function goToSuccess() {
    paymentSuccessRef.current.open();
  }

  function goToFailure() {
    paymentFailureRef.current.open();
  }

  const checkoutCartOrder = async (method) => {
    cleanInput(orderData);
    const response = await dispatch(
      checkoutPublicCart({
        ...orderData,
        payment_method: method === "transfer" ? "paystack" : method,
        payment_type: "standard",
        address:
          orderData?.address ??
          "Suite D5 Bethel Plaza, 36 Garden Ave, Enugu 400261",
      })
    );

    if (response?.status === "success") {
      getReferenceId(response?.data?.id);
      getTotalPrice(response?.data?.amount);
      const new_response = await dispatch(
        storePublicCartProducts({
          ...orderData,
          products: null,
          packages: null,
          checkout_codes: null,
        })
      );
      localStorage.setItem(
        "ogwugo-cart-items",
        JSON.stringify(new_response?.data)
      );
      localStorage.setItem("ogwugo-cart", JSON.stringify(new_response.data));

      if (method === "transfer") {
        paystackTransferRef.current.getAmount_Reference(
          response?.data?.provider?.paystack?.amount,
          response?.data?.id
        );
      }

      if (method === "paystack") {
        paystackRef.current.getAmount_Reference(
          response?.data?.provider?.paystack?.amount,
          response?.data?.id
        );
      }

      if (method === "flutterwave") {
        flutterRef.current.getAmount_Reference(
          response?.data?.provider?.flutterwave?.amount,
          response?.data?.id
        );
      }
    }
  };

  return (
    <React.Fragment>
      {/* <div className="">
        {primaryButton ? (
          <span className="cursor-pointer" onClick={openModal}>
            {primaryButton}
          </span>
        ) : (
          <button
            type="button"
            style={primaryButtonStyle}
            onClick={openModal}
            disabled={primaryButtonStyle?.disabled}
            className={
              "glade-normal-text-three glade-button-blue whitespace-nowrap w-11/12 py-2 px-4 flex justify-center items-center text-white text-center rounded-sm " +
              primaryButtonClassName
            }
          >
            {primaryButtonTitle}
          </button>
        )}
      </div> */}

      <Transition appear show={visibility || isOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-full p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-[629px] bg-white dark:bg-[#121212] rounded-sm px-4 pt-5 pb-4  overflow-hidden shadow-xl transform transition-all sm:my-10 sm:max-w-lg sm:w-full  sm:p-8">
                  <div className="sm:block absolute top-2 right-3 pt-4 pr-4">
                    <button type="button" className="" onClick={closeModal}>
                      <span className="sr-only">Close</span>
                      <img
                        src={CLOSE}
                        alt="close"
                        className=" w-3 h-3 sm:w-4 sm:h-4"
                      />
                    </button>
                  </div>
                  <div className="sm:flex sm:items-start">
                    <div className="text-center mx-auto ">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-meduim text-black dark:text-white"
                      >
                        How would you like to pay?
                      </Dialog.Title>
                    </div>
                  </div>
                  <div className=" border-b-2 "></div>
                  <div className="mt-6 flex gap-2 items-center justify-center">
                    <img
                      src={Warning}
                      alt="warning"
                      className="w-[18px] h-[18px] cursor-pointer"
                    />
                    <p className="text-md">
                      "If your card hasn't been enabled for online transactions,
                      kindly opt for a bank transfer as an alternative."
                    </p>
                  </div>

                  <div className="mt-6 sm:mt-2">
                    <div className="mt-5">
                      {isLoading ? (
                        <PaymentSkeleton />
                      ) : (
                        <ul>
                          {settings?.ogwugo_payment_status === "available" && (
                            <li
                              className="flex mb-4 bg-white dark:bg-[#121212] h-20 shadow py-4 text-left dark:border dark:border-[#BCBCBC] rounded-md cursor-pointer hover:bg-gray-100 group hover:shadow"
                              onClick={goToBank}
                            >
                              <img
                                src={LOGO}
                                alt="Ogwugo logo"
                                className="bg-gray-100 p-2 ml-3 w-12 h-12 rounded-lg "
                              />
                              <div className="ml-3 overflow-hidden ">
                                <p className=" text-lg font-meduim text-black dark:text-white ">
                                  Ogwugo Pay
                                </p>
                                <p className="text-base text-[#676565] dark:text-[#BCBCBC] font-normal">
                                  Transfer into Ogwugo account
                                </p>
                              </div>
                            </li>
                          )}

                          {settings?.pay_stack_payment_status === "available" &&
                            allProviders?.pay_stack_key && (
                              <li
                                className="flex mb-4 bg-white dark:bg-[#121212] h-20 shadow py-4 text-left dark:border dark:border-[#BCBCBC] rounded-md cursor-pointer hover:bg-gray-100 group hover:shadow"
                                onClick={() => checkoutCartOrder("transfer")}
                              >
                                <img
                                  src={TRANSFER}
                                  alt="logo"
                                  className="bg-gray-100 p-2 ml-3 w-12 h-12 rounded-lg "
                                />
                                <div className="ml-3 overflow-hidden ">
                                  <p className=" text-lg font-meduim text-black dark:text-white ">
                                    Bank Transfer
                                  </p>
                                  <p className="text-base text-[#676565] dark:text-[#BCBCBC] font-normal">
                                    Transfer into Ogwugo account
                                  </p>
                                </div>
                              </li>
                            )}
                          <PaystackTransferPlugin
                            amount={totalPrice}
                            email={orderData?.email}
                            reference={reference_id}
                            payment_type={"standard"}
                            onClose={goToFailure}
                            onSuccess={goToSuccess}
                            ref={paystackTransferRef}
                          />

                          {settings?.pay_stack_payment_status === "available" &&
                            allProviders?.pay_stack_key && (
                              <li
                                onClick={() => checkoutCartOrder("paystack")}
                                className="flex mb-4 bg-white dark:bg-[#121212] h-20 shadow py-4 text-left dark:border dark:border-[#BCBCBC] rounded-md cursor-pointer hover:bg-gray-100 group hover:shadow"
                              >
                                <img
                                  src={PAYSTACK}
                                  alt="payStack logo"
                                  className="bg-gray-100 p-4 ml-3 w-12 h-12 rounded-lg "
                                />
                                <div className="ml-3 overflow-hidden ">
                                  <p className=" text-lg font-meduim text-black dark:text-white ">
                                    Paystack
                                  </p>
                                  <p className="text-base text-[#676565] dark:text-[#BCBCBC] font-normal">
                                    Pay with Card, USSD, Bank Transfer or Visa
                                    QR
                                  </p>
                                </div>
                              </li>
                            )}

                          <PaystackPlugin
                            amount={totalPrice}
                            email={
                              orderData?.email
                                ? orderData?.email
                                : receipt_email
                                ? receipt_email
                                : "hello@ogwugo.com"
                            }
                            onClose={() => {}}
                            onSuccess={goToSuccess}
                            payment_type={payment_type}
                            reference={reference_id}
                            ref={paystackRef}
                          />

                          {settings?.flutter_wave_payment_status ===
                            "available" &&
                            allProviders?.flutter_wave_key && (
                              <li
                                onClick={() => checkoutCartOrder("flutterwave")}
                                className="flex mb-4 bg-white dark:bg-[#121212] h-20 shadow py-4 text-left dark:border dark:border-[#BCBCBC] rounded-md cursor-pointer hover:bg-gray-100 group hover:shadow"
                              >
                                <img
                                  src={FLUTTER}
                                  alt="Ogwugo o logo"
                                  className="bg-gray-100 p-2 ml-3 w-12 h-12 rounded-lg "
                                />
                                <div className="ml-3 overflow-hidden">
                                  <p className=" text-lg font-meduim text-black dark:text-white ">
                                    Flutterwave
                                  </p>
                                  <p className="text-base text-[#676565] dark:text-[#BCBCBC] font-normal">
                                    Pay with Card, USSD, Bank Transfer.
                                  </p>
                                </div>
                              </li>
                            )}

                          <FlutterwavePlugin
                            amount={totalPrice}
                            email={
                              orderData?.email
                                ? orderData?.email
                                : receipt_email
                                ? receipt_email
                                : "hello@ogwugo.com"
                            }
                            phone_number={orderData?.phone}
                            name={orderData?.name}
                            tx_ref={reference_id}
                            payment_type={payment_type}
                            onClose={() => {}}
                            onSuccess={goToSuccess}
                            ref={flutterRef}
                          />
                        </ul>
                      )}
                    </div>
                    <PaymentSuccess ref={paymentSuccessRef} paymentRef={ref} />
                    <PaymentFailure ref={paymentFailureRef} paymentRef={ref} />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </React.Fragment>
  );
});
